import { Component, ViewChild  } from "@angular/core";
import {
    MatTableDataSource,
    MatTableModule,
    MatTable,
} from "@angular/material/table";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { Bug } from "../../interfaces/bug";
import { FormsModule } from "@angular/forms";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
    animate,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import { MatIconModule } from "@angular/material/icon";
import { AsyncPipe } from "@angular/common";
import { ReportInterventionButtonComponent } from "../../components/report-intervention-button/report-intervention-button";
import { firstValueFrom, take } from "rxjs";
import { AdoBugsService } from "../../services/bugs_service/bugs.service";
import { GetImageUrlPipe } from "../../pipes/get-image-url.pipe";
import { AutocompleteSearchComponent } from "../../components/autocomplete-search/autocomplete-search.component";
import { FilterListComponent } from "../../components/filter-list/filter-list.component";
import { environment } from "../../environments/environment";
import { HW_COMPONENTS } from "../../constants/hw-components.const";
import { MsalService } from "@azure/msal-angular";
import { RoleService } from "../../services/role_service/role.service";
import { UserRole } from "../../enums/user-role.enum";

/**
 * @title Table with filtering
 */
@Component({
    selector: "bug-list",
    styleUrl: "bug-list.component.scss",
    templateUrl: "bug-list.component.html",
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        FilterListComponent,
        AutocompleteSearchComponent,
        MatSelectModule,
        FormsModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatIconModule,
        GetImageUrlPipe,
        AsyncPipe,
        ReportInterventionButtonComponent
    ],
    animations: [
        trigger("detailExpand", [
            state("collapsed,void", style({ height: "0px", minHeight: "0" })),
            state("expanded", style({ height: "*" })),
            transition(
                "expanded <=> collapsed",
                animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)"),
            ),
        ]),
    ],
})
export class BugListComponent {
    // TODO: Merge these to one map.
    //  Wasn't able to do this, but should make it easier with {key: display_name}
    propertiesToDisplay: string[] = ["id", "title", "latestSwFoundIn", "fixedInVersion", "totalDowntime", "numberOfReports"];
    propertyHeaderMap: Map<string, string> = new Map([
        ["id", "ID"],
        ["title", "Title"],
        ["latestSwFoundIn", "Found in version"],
        ["fixedInVersion", "Fixed in version"],
        ["totalDowntime", "Total Downtime"],
        ["numberOfReports", "Number of Reports"],
    ]);
    propertiesToDisplayWithExpand = [...this.propertiesToDisplay, "expand"];
    adoProject = environment.adoApiConfig.project;
    allBugs: Bug[];
    filteredBugs: Bug[];
    dataSource = new MatTableDataSource<Bug>();
    expandedElement: Bug | null;
    dataSourceHwVersions = new MatTableDataSource<string>();
    dataSourceIssueGroups = new MatTableDataSource<string>();
    hwVersions: string[] = HW_COMPONENTS;
    hwVersionFilters: string[] = [];
    issueGroupFilters: string[] = [];
    loading = true;
    noBugs = false;

    showTable = false;
    showAdoButton = false;

    @ViewChild(MatTable) table!: MatTable<Bug>;
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        public bugsService: AdoBugsService,
        public msalService: MsalService,
        public roleService: RoleService
    ) { }

    ngOnInit() {
        this._setViewParameters(this.roleService.getRole());

        if (this.showTable) {
            this.bugsService.getAllActiveBugs().pipe(take(1)).subscribe((bugs) => {
                if (bugs) {
                    this.allBugs = bugs;
                    this.filteredBugs = bugs;
                    this.dataSource.data = this.filteredBugs;
                    this.table.renderRows();

                    this.dataSourceHwVersions.data = this.hwVersions;
                    this.dataSourceIssueGroups.data = this.getUniquePropertiesFromBugs(
                        bugs,
                        "issueGroup",
                    );

                } else { 
                    this.noBugs = true;
                }
                this.loading = false;
            });
        } else { 
            this.loading = false;
        }
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    getUniquePropertiesFromBugs(bugs: Bug[], property: string) {
        let uniqueProperties: Set<string> = new Set();
        try {
            for (const bug of bugs) {
                if ((bug as { [index: string]: any })[property] !== undefined) {
                    uniqueProperties.add((bug as { [index: string]: any })[property]);
                }
            }
        } catch (error) {
            console.error(`Failed to fetch work item details: ${error}`);
        } finally {
            return Array.from(uniqueProperties);
        }
    }

    applySearchFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.toLowerCase();
        this.applyAllFilters();
    }

    applyAllFilters() {
        let commonItems = [...this.allBugs];
        if (this.hwVersionFilters.length > 0) {
            commonItems = commonItems.filter(
                (item) =>
                    item.hwVersion &&
                    this.hwVersionFilters.some((filter) =>
                        item.hwVersion?.includes(filter)
                    ),
            );
        }

        if (this.issueGroupFilters.length > 0) {
            commonItems = commonItems.filter((item) =>
                item.issueGroup && this.issueGroupFilters.some((filter) =>
                    item.issueGroup?.includes(filter)
                ),
            );
        }
        if (
            this.dataSource.filteredData.length > 0 &&
            this.dataSource.filteredData.length !== this.dataSource.data.length
        ) {
            commonItems = commonItems.filter((item) =>
                this.dataSource.filteredData.includes(item),
            );
        }

        this.dataSource.data = commonItems;
        this.table.renderRows();
    }

    addHwVersionFilter(event: any) {
        if (typeof event === "string") {
            this.hwVersionFilters = [event];
        } else if (
            Array.isArray(event) &&
            event.every((item) => typeof item === "string")
        ) {
            this.hwVersionFilters = event;
        } else {
            console.error("Invalid event:", event);
        }

        this.applyAllFilters();
    }

    addIssueGroupFilter(event: any) {
        if (typeof event === "string") {
            this.issueGroupFilters = [event];
        } else if (
            Array.isArray(event) &&
            event.every((item) => typeof item === "string")
        ) {
            this.issueGroupFilters = event;
        } else {
            console.error("Invalid event:", event);
        }

        this.applyAllFilters();
    }

    _setViewParameters(role: UserRole) { 
        if (role == UserRole.Contractor) { 
            this.showTable = true;
        } else if (role == UserRole.SuperUser) { 
            this.showAdoButton = true;
            this.showTable = true;
        } 
    }
}
